import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle2, XCircle, AlertOctagon, GitCompareArrows,
  HelpCircle, FileText, LogOut, Download, RefreshCcw, ChevronRight,
  Building2, ShieldCheck, ClipboardList, Presentation, FileCheck2,
  Send, ExternalLink, Wrench,
} from "lucide-react";

import { testApi, API, getTestToken } from "@/lib/testApi";

/**
 * WP-G · Authenticated Company Workspace.
 *
 * The company workflow is substantively different from the investor
 * pathway. It answers: which impact and ESG claims can the company
 * substantiate, what evidence is missing or contradictory, what should be
 * corrected before diligence, and what can be presented credibly to
 * investors.
 *
 * Six client-visible stages, wired to real backend endpoints:
 *   1. Evidence intake              → GET /test/company/opportunity
 *   2. Claim register               → GET /test/opportunities/{id}/claims
 *   3. Substantiation review        → GET /test/opportunities/{id}/evidence-pack
 *   4. Claim strengthening          → WP-B revision endpoints
 *   5. Investor-readiness actions   → PATCH /test/opportunities/{id}/readiness/{action_id}
 *   6. Pack review and release      → GET/POST /test/opportunities/{id}/evidence-pack + versions
 *
 * Guardrails:
 *   * No fabricated market statistics, ticket-size expectations or
 *     performance benchmarks.
 *   * No certification, assurance, verification or compliance language.
 *   * Framework references are evidence organisation only.
 *   * Watermarks on every payload; every AI-derived suggestion requires
 *     explicit human approval (never silently applied).
 */

const STAGES = [
  { key: "intake",       label: "Evidence intake",       short: "Intake",         icon: FileText },
  { key: "register",     label: "Claim register",        short: "Register",       icon: ClipboardList },
  { key: "substantiate", label: "Substantiation",        short: "Substantiate",   icon: FileCheck2 },
  { key: "strengthen",   label: "Claim strengthening",   short: "Strengthen",     icon: Wrench },
  { key: "readiness",    label: "Investor readiness",    short: "Readiness",      icon: ShieldCheck },
  { key: "pack",         label: "Evidence Pack",         short: "Pack",           icon: Presentation },
];

const STATUS_META = {
  supported:            { label: "Supported",             tone: "green",  icon: CheckCircle2 },
  partially_supported:  { label: "Partially supported",   tone: "gold",   icon: AlertOctagon },
  unsupported:          { label: "Unsupported",           tone: "red",    icon: XCircle },
  conflicting_evidence: { label: "Conflicting evidence",  tone: "crimson", icon: GitCompareArrows },
  insufficient_evidence:{ label: "Insufficient evidence", tone: "grey",   icon: HelpCircle },
};

const PROBLEM_CODES = [
  "unsupported", "overstated", "ambiguous_scope", "missing_baseline",
  "missing_methodology", "missing_boundary", "missing_reporting_period",
  "output_labelled_as_outcome", "mislabelled_target_as_result",
  "cherry_picked_reporting_period", "unsupported_environmental_claim", "other",
];

export default function CompanyWorkspace({ me, onSignOut }) {
  const [stage, setStage] = useState("intake");
  const [opp, setOpp] = useState(null);
  const [claims, setClaims] = useState([]);
  const [pack, setPack] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [revisionsByClaim, setRevisionsByClaim] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { document.title = "ImpactVest · Company Workspace"; }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Ensure the company opportunity exists.
      let oppData = null;
      try {
        const r = await testApi.get("/test/company/opportunity");
        oppData = r.data.opportunity;
      } catch (e) {
        if (e?.response?.status === 404) {
          const r = await testApi.post("/test/company/seed");
          oppData = r.data.opportunity;
        } else {
          throw e;
        }
      }
      setOpp(oppData);
      // 2. Claims + pack in parallel
      const [claimsRes, packRes, versionsRes] = await Promise.all([
        testApi.get(`/test/opportunities/${oppData.id}/claims`),
        testApi.get(`/test/opportunities/${oppData.id}/evidence-pack`),
        testApi.get(`/test/opportunities/${oppData.id}/evidence-pack/versions`),
      ]);
      const cl = claimsRes.data.claims || [];
      setClaims(cl);
      setPack(packRes.data);
      setVersions(versionsRes.data.versions || []);
      if (cl.length && !selectedClaimId) setSelectedClaimId(cl[0].id);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Unable to load the company workspace.");
    } finally {
      setLoading(false);
    }
    // ensure loadAll is only fired on mount
  }, []); // eslint-disable-line

  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshPack = useCallback(async () => {
    if (!opp) return;
    try {
      const [claimsRes, packRes, versionsRes] = await Promise.all([
        testApi.get(`/test/opportunities/${opp.id}/claims`),
        testApi.get(`/test/opportunities/${opp.id}/evidence-pack`),
        testApi.get(`/test/opportunities/${opp.id}/evidence-pack/versions`),
      ]);
      setClaims(claimsRes.data.claims || []);
      setPack(packRes.data);
      setVersions(versionsRes.data.versions || []);
    } catch (e) { void e; }
  }, [opp]);

  const loadRevisions = useCallback(async (claimId) => {
    if (!opp || !claimId) return;
    try {
      const r = await testApi.get(`/test/opportunities/${opp.id}/claims/${claimId}/revisions`);
      setRevisionsByClaim((s) => ({ ...s, [claimId]: r.data.revisions || [] }));
    } catch (e) { void e; }
  }, [opp]);

  useEffect(() => {
    if (selectedClaimId) loadRevisions(selectedClaimId);
  }, [selectedClaimId, loadRevisions]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-iv-bg">
        <span className="font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">Loading company workspace…</span>
      </div>
    );
  }
  if (!opp) return null;

  const counts = pack?.executive_summary?.counts || {};
  const releaseReady = !!pack?.release_gates?.ready;

  return (
    <div className="min-h-screen bg-iv-bg" data-testid="company-workspace">
      {/* Workspace disclaimer bar */}
      <div className="border-b border-[var(--ivc-line-warm)] bg-[var(--ivc-bg)] text-[var(--ivc-ivory-2)]">
        <div className="iv-container flex items-center justify-between h-[38px]">
          <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--ivc-gold-2)]">
            Company Workspace · Fictional demonstration · Not a live company disclosure
          </span>
          <div className="flex items-center gap-4">
            <button onClick={onSignOut}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)] hover:text-[var(--ivc-ivory)]"
              data-testid="company-signout">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Opportunity header */}
      <div className="border-b border-iv-line bg-iv-surface">
        <div className="iv-container flex items-center gap-3 min-w-0 py-3">
          <Building2 size={16} strokeWidth={1.7} className="text-iv-fg-2 shrink-0" />
          <span className="font-serif-display text-[19px] text-iv-fg truncate" data-testid="company-opp-name">{opp.name}</span>
          <span className="hidden md:inline font-mono text-[11px] tracking-[0.16em] uppercase text-iv-fg-3">
            {opp.stage} · {opp.proposed_amount}
          </span>
        </div>
      </div>

      {/* Sticky stage nav — wraps at narrow desktop widths so no label clips */}
      <div className="border-b border-iv-line bg-iv-surface sticky top-0 z-40">
        <div className="iv-container">
          <div className="hidden md:flex flex-wrap items-center gap-x-1 gap-y-0 py-1.5">
            {STAGES.map((s) => (
              <button key={s.key} data-testid={`company-tab-${s.key}`}
                onClick={() => setStage(s.key)}
                aria-current={stage === s.key ? "page" : undefined}
                className={`px-3 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap inline-flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-iv-green/50 ${stage === s.key ? "border-iv-green text-iv-fg" : "border-transparent text-iv-fg-2 hover:text-iv-fg"}`}>
                <s.icon size={14} /> {s.label}
              </button>
            ))}
          </div>
          {/* Mobile stage picker */}
          <div className="md:hidden py-2">
            <select value={stage} onChange={(e) => setStage(e.target.value)}
              className="w-full text-[13.5px] font-mono py-2 px-2 bg-transparent border border-iv-line rounded-[2px]"
              data-testid="company-stage-mobile">
              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <main className="iv-container py-8 md:py-12">
        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-8" data-testid="company-summary-strip">
          <SummaryCard k="Supported" v={counts.supported || 0} tone="green" tid="pack-signal-supported" />
          <SummaryCard k="Partially" v={counts.partially_supported || 0} tone="gold" tid="pack-signal-partial" />
          <SummaryCard k="Unsupported" v={counts.unsupported || 0} tone="red" tid="pack-signal-unsupported" />
          <SummaryCard k="Conflicting" v={counts.conflicting_evidence || 0} tone="crimson" tid="pack-signal-conflict" />
          <SummaryCard k="Insufficient" v={counts.insufficient_evidence || 0} tone="grey" tid="pack-signal-insufficient" />
          <SummaryCard k="Release" v={releaseReady ? "Ready" : "Release blocked"} tone={releaseReady ? "green" : "crimson"} tid="pack-signal-release" />
        </div>

        {stage === "intake" && (
          <StageIntake opp={opp} />
        )}
        {stage === "register" && (
          <StageRegister claims={claims} selected={selectedClaimId} onSelect={(id) => { setSelectedClaimId(id); setStage("substantiate"); }} />
        )}
        {stage === "substantiate" && (
          <StageSubstantiate
            opp={opp}
            claims={claims}
            selectedId={selectedClaimId}
            onSelect={setSelectedClaimId}
            onResolutionChanged={refreshPack}
          />
        )}
        {stage === "strengthen" && (
          <StageStrengthen
            opp={opp}
            claims={claims}
            selectedId={selectedClaimId}
            onSelect={setSelectedClaimId}
            revisionsByClaim={revisionsByClaim}
            reloadRevisions={loadRevisions}
            onChange={refreshPack}
            busy={busy}
            setBusy={setBusy}
          />
        )}
        {stage === "readiness" && (
          <StageReadiness opp={opp} onUpdate={refreshPack} />
        )}
        {stage === "pack" && (
          <StagePack opp={opp} pack={pack} versions={versions} onRelease={refreshPack}
                     onResolveNavigate={(claimId) => { setSelectedClaimId(claimId); setStage("substantiate"); }} />
        )}
      </main>
    </div>
  );
}

function SummaryCard({ k, v, tone, tid }) {
  const toneClass = {
    green:   "border-l-[3px] border-[#0E5A3E]",
    gold:    "border-l-[3px] border-[#B39352]",
    red:     "border-l-[3px] border-[#8A3324]",
    crimson: "border-l-[3px] border-[#7B2D26]",
    grey:    "border-l-[3px] border-[#6B6558]",
  }[tone] || "";
  return (
    <div className={`bg-iv-surface p-3 ${toneClass}`} data-testid={tid}>
      <div className="font-serif-display text-[22px] leading-tight text-iv-fg">{v}</div>
      <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-2 mt-0.5">{k}</div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Stage 1 · Evidence intake
 * ----------------------------------------------------------------------- */
function StageIntake({ opp }) {
  return (
    <section data-testid="stage-intake">
      <div className="iv-eyebrow mb-6">Stage 1 · Evidence intake</div>
      <h2 className="font-serif-display text-[30px] md:text-[42px] leading-tight text-iv-fg max-w-3xl">
        The documents in scope for this evidence pack.
      </h2>
      <p className="mt-5 text-[15px] leading-relaxed text-iv-fg-2 max-w-2xl">
        The claim register is derived from the documents listed below. This fictional demonstration
        illustrates how ImpactVest connects company claims to their supporting evidence, identifies
        inconsistencies and prepares the record for investor diligence.
      </p>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
        {opp.documents.map((d) => (
          <div key={d.id} className="bg-iv-surface p-4 border border-iv-line" data-testid={`intake-doc-${d.id}`}>
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-2">{d.kind?.toUpperCase()}</div>
            <div className="text-[15px] text-iv-fg mt-1.5 leading-snug">{d.name}</div>
            <div className="mt-2 text-[13px] text-iv-fg-2">Provided by: {d.provided_by || "Company"}</div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[12.5px] italic text-iv-fg-3 max-w-2xl">
        All organisations, documents and figures shown in this demonstration are fictional and included
        for illustrative purposes only.
      </p>
      <div className="mt-8 iv-canvas rounded-[3px] p-6 max-w-3xl">
        <div className="iv-canvas-eyebrow mb-3">Thesis (as prepared for investors)</div>
        <p className="text-[14.5px] leading-relaxed text-[var(--ivc-ivory-2)]">{opp.thesis}</p>
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------
 * Stage 2 · Claim register
 * ----------------------------------------------------------------------- */
function StageRegister({ claims, selected, onSelect }) {
  const groups = useMemo(() => {
    const g = { supported: [], partially_supported: [], unsupported: [], conflicting_evidence: [], insufficient_evidence: [] };
    for (const c of claims) g[c.status]?.push(c);
    return g;
  }, [claims]);

  return (
    <section data-testid="stage-register">
      <div className="iv-eyebrow mb-6">Stage 2 · Claim register</div>
      <h2 className="font-serif-display text-[30px] md:text-[42px] leading-tight text-iv-fg max-w-3xl">
        Every claim mapped to its evidence status.
      </h2>
      <p className="mt-5 text-[14.5px] leading-relaxed text-iv-fg-2 max-w-2xl">
        The five statuses are recorded per claim by the analysis engine and refined by workspace users during
        substantiation review. No composite readiness score is produced.
      </p>

      <div className="mt-8 space-y-8">
        {Object.entries(groups).map(([status, list]) => {
          if (!list.length) return null;
          const meta = STATUS_META[status];
          const Icon = meta.icon;
          return (
            <div key={status} data-testid={`register-group-${status}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} className="text-iv-fg-2" />
                <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-iv-fg-2">{meta.label} ({list.length})</div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {list.map((c) => (
                  <button key={c.id} onClick={() => onSelect(c.id)}
                    data-testid={`register-claim-${c.id}`}
                    className={`text-left border p-4 hover:border-iv-green transition-colors ${selected === c.id ? "border-iv-green bg-iv-surface" : "border-iv-line bg-iv-surface/60"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="text-[14px] text-iv-fg leading-relaxed">{c.effective_label || c.label}</div>
                      <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-iv-fg-3 whitespace-nowrap">Materiality: {c.materiality || "Medium"}</div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[12px] text-iv-fg-3">
                      <span>{c.citations?.length || 0} citation(s)</span>
                      <ChevronRight size={12} />
                      <span>Open substantiation view</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------
 * Stage 3 · Substantiation review (single claim detail)
 * ----------------------------------------------------------------------- */
function StageSubstantiate({ opp, claims, selectedId, onSelect, onResolutionChanged }) {
  const c = claims.find((x) => x.id === selectedId) || claims[0];
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolutionStatus, setResolutionStatus] = useState("open");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (c?.resolution) {
      setResolutionStatus(c.resolution.status || "open");
      setResolutionNote(c.resolution.note || "");
    } else {
      setResolutionStatus("open");
      setResolutionNote("");
    }
  }, [c?.id]); // eslint-disable-line

  if (!c) return <div>No claim selected.</div>;

  const meta = STATUS_META[c.status] || STATUS_META.insufficient_evidence;
  const Icon = meta.icon;

  const saveResolution = async () => {
    setBusy(true);
    try {
      await testApi.patch(`/test/opportunities/${opp.id}/claims/${c.id}`, {
        resolution_status: resolutionStatus,
        resolution_note: resolutionNote,
      });
      toast.success("Resolution recorded.");
      onResolutionChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to save resolution.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="stage-substantiate">
      <div className="iv-eyebrow mb-6">Stage 3 · Substantiation review</div>
      <div className="grid grid-cols-12 gap-6">
        {/* Left · list */}
        <div className="col-span-12 md:col-span-4 border border-iv-line bg-iv-surface p-3 max-h-[70vh] overflow-y-auto">
          <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3 mb-2">Claims</div>
          <div className="space-y-1">
            {claims.map((cc) => {
              const m = STATUS_META[cc.status] || STATUS_META.insufficient_evidence;
              return (
                <button key={cc.id} onClick={() => onSelect(cc.id)}
                  data-testid={`sub-list-${cc.id}`}
                  className={`w-full text-left p-2.5 border-l-[3px] ${cc.id === c.id ? "bg-iv-bg border-iv-green" : "border-transparent hover:bg-iv-bg-2"}`}>
                  <div className="text-[13px] text-iv-fg leading-snug line-clamp-2">{cc.effective_label || cc.label}</div>
                  <div className="mt-1 font-mono text-[9.5px] tracking-[0.14em] uppercase text-iv-fg-3">{m.label}</div>
                </button>
              );
            })}
          </div>
        </div>
        {/* Right · detail */}
        <div className="col-span-12 md:col-span-8" data-testid={`sub-detail-${c.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <Icon size={14} />
            <div className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-iv-fg-2">{meta.label} · Materiality {c.materiality || "Medium"}</div>
          </div>
          <h3 className="font-serif-display text-[24px] md:text-[30px] leading-tight text-iv-fg">{c.effective_label || c.label}</h3>
          {c.effective_label && c.effective_label !== c.label && (
            <div className="mt-2 text-[13px] text-iv-fg-3 italic">Original wording: “{c.label}”</div>
          )}
          <p className="mt-4 text-[14.5px] leading-relaxed text-iv-fg-2">{c.evidence_status_rationale}</p>

          {/* Citations */}
          <div className="mt-6">
            <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3 mb-2">Evidence citations</div>
            {(c.citations && c.citations.length) ? (
              <div className="space-y-2">
                {c.citations.map((ct, i) => (
                  <div key={i} className="bg-iv-surface border-l-[3px] border-iv-gold p-3">
                    <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">{ct.document_name} · {ct.location}</div>
                    <div className="mt-1.5 text-[13.5px] text-iv-fg italic">“{ct.excerpt}”</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[13px] text-[#8A3324] italic">No citation from the uploaded documents. This claim cannot be presented to investors without evidence.</div>
            )}
          </div>

          {/* Provenance grid */}
          <div className="mt-6 border border-iv-line">
            <ProvenanceRow k="Definition" v={c.definition} />
            <ProvenanceRow k="Baseline" v={c.baseline} />
            <ProvenanceRow k="Boundary" v={c.boundary} />
            <ProvenanceRow k="Methodology" v={c.methodology} />
            <ProvenanceRow k="Reporting period" v={c.reporting_period} />
            <ProvenanceRow k="Evidence owner" v={c.evidence_owner} />
          </div>

          {/* Missing info + recommended action */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-iv-surface p-4 border border-iv-line">
              <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3 mb-2">Missing information</div>
              {(c.missing_information && c.missing_information.length) ? (
                <ul className="text-[13px] text-iv-fg-2 space-y-1 list-disc pl-4">
                  {c.missing_information.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              ) : <div className="text-[13px] text-iv-fg-3">None recorded.</div>}
            </div>
            <div className="bg-[rgba(14,90,62,0.06)] border-l-[3px] border-iv-green p-4">
              <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3 mb-2">Recommended next action</div>
              <div className="text-[13.5px] text-iv-fg-2 leading-relaxed">{c.recommended_next_action || "—"}</div>
            </div>
          </div>

          {/* Framework references — evidence organisation only */}
          {c.framework_mappings?.length ? (
            <div className="mt-6">
              <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3 mb-2">Framework references · evidence organisation only</div>
              <ul className="text-[13px] text-iv-fg-2 space-y-1">
                {c.framework_mappings.map((fm, i) => (
                  <li key={i}><span className="font-medium">{fm.framework}</span> — {fm.note}</li>
                ))}
              </ul>
              <div className="mt-2 text-[11.5px] italic text-iv-fg-3">
                Framework references show which disclosure frameworks the evidence would organise against.
                They are not certification, verification, assurance or a compliance opinion.
              </div>
            </div>
          ) : null}

          {/* Resolution controls (contradictory claims) */}
          {c.category === "contradictory_evidence" && (
            <div className="mt-6 bg-iv-surface border border-iv-line p-4" data-testid="sub-resolution">
              <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3 mb-3">Contradiction resolution</div>
              <div className="flex flex-wrap items-center gap-2">
                {["open", "reviewed", "resolved"].map((s) => (
                  <button key={s} data-testid={`sub-res-${s}`}
                    onClick={() => setResolutionStatus(s)}
                    className={`px-3 py-1.5 text-[12px] font-mono tracking-[0.14em] uppercase border ${resolutionStatus === s ? "border-iv-green text-iv-fg" : "border-iv-line text-iv-fg-3 hover:text-iv-fg"}`}>
                    {s}
                  </button>
                ))}
              </div>
              <textarea value={resolutionNote} onChange={(e) => setResolutionNote(e.target.value)}
                data-testid="sub-res-note"
                placeholder="Record the workspace decision (e.g. which figure was retained and why)…"
                className="mt-3 w-full min-h-[80px] p-2.5 bg-iv-bg border border-iv-line text-[13px] rounded-[2px]" />
              <div className="mt-3 flex justify-end">
                <button data-testid="sub-res-save" onClick={saveResolution} disabled={busy}
                  className="iv-btn iv-btn--primary text-[12.5px]">
                  Save resolution
                </button>
              </div>
              <div className="mt-2 text-[11.5px] italic text-iv-fg-3">
                Unresolved material contradictions block release. High-materiality contradictions must be recorded as
                resolved before the pack can be released.
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ProvenanceRow({ k, v }) {
  return (
    <div className="grid grid-cols-12 border-t border-iv-line">
      <div className="col-span-12 md:col-span-3 p-2.5 bg-iv-bg-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">{k}</div>
      <div className="col-span-12 md:col-span-9 p-2.5 text-[13px] text-iv-fg-2">{v || "—"}</div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Stage 4 · Claim strengthening (WP-B revisions)
 * ----------------------------------------------------------------------- */
function StageStrengthen({ opp, claims, selectedId, onSelect, revisionsByClaim, reloadRevisions, onChange, busy, setBusy }) {
  const c = claims.find((x) => x.id === selectedId) || claims[0];
  const [draft, setDraft] = useState({ suggested: "", reason: "", problem_code: "unsupported" });
  useEffect(() => { setDraft({ suggested: "", reason: "", problem_code: "unsupported" }); }, [c?.id]);
  if (!c) return null;

  const revisions = revisionsByClaim[c.id] || [];

  const create = async () => {
    if (!draft.suggested.trim() || !draft.reason.trim()) {
      toast.error("Provide both a suggested wording and a reason before submitting.");
      return;
    }
    setBusy(true);
    try {
      await testApi.post(`/test/opportunities/${opp.id}/claims/${c.id}/revisions`, draft);
      toast.success("Revision suggestion recorded. Nothing has changed on the claim itself until you approve it.");
      setDraft({ suggested: "", reason: "", problem_code: "unsupported" });
      await reloadRevisions(c.id);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Unable to record revision.");
    } finally { setBusy(false); }
  };

  const transition = async (revId, status, final_wording) => {
    setBusy(true);
    try {
      await testApi.patch(`/test/opportunities/${opp.id}/claims/${c.id}/revisions/${revId}`,
        status === "user_edited" ? { status, final_wording } : { status });
      toast.success(`Revision ${status}. Human approval recorded with your account email.`);
      await reloadRevisions(c.id);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to update revision.");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="stage-strengthen">
      <div className="iv-eyebrow mb-6">Stage 4 · Claim strengthening</div>
      <h2 className="font-serif-display text-[28px] md:text-[38px] leading-tight text-iv-fg max-w-3xl">
        Suggest a stronger wording. Require human approval before it takes effect.
      </h2>
      <p className="mt-4 text-[14px] text-iv-fg-2 max-w-2xl">
        A suggestion never replaces the original claim silently. Only when you (or a colleague you name in
        the audit trail) explicitly approve — or hand-edit — does the pack render the revised wording.
      </p>

      <div className="mt-6 grid grid-cols-12 gap-6">
        {/* Left · claim picker */}
        <div className="col-span-12 md:col-span-4 border border-iv-line bg-iv-surface p-3 max-h-[60vh] overflow-y-auto">
          <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3 mb-2">Pick a claim</div>
          <div className="space-y-1">
            {claims.map((cc) => (
              <button key={cc.id} onClick={() => onSelect(cc.id)}
                data-testid={`strengthen-list-${cc.id}`}
                className={`w-full text-left p-2.5 text-[12.5px] ${cc.id === c.id ? "bg-iv-bg border-l-[3px] border-iv-green" : "border-l-[3px] border-transparent hover:bg-iv-bg-2"}`}>
                <div className="line-clamp-2 text-iv-fg">{cc.effective_label || cc.label}</div>
                <div className="mt-1 font-mono text-[9.5px] tracking-[0.14em] uppercase text-iv-fg-3">{STATUS_META[cc.status]?.label}</div>
              </button>
            ))}
          </div>
        </div>
        {/* Right · revision workshop */}
        <div className="col-span-12 md:col-span-8" data-testid={`strengthen-detail-${c.id}`}>
          <div className="bg-iv-surface border border-iv-line p-5">
            <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3">Original wording</div>
            <div className="mt-1 text-[15px] text-iv-fg">{c.label}</div>
            {c.effective_label && c.effective_label !== c.label && (
              <div className="mt-2 text-[13px] text-iv-fg-2 italic">Current effective wording (from approved revision): “{c.effective_label}”</div>
            )}
          </div>

          <div className="mt-5 p-5 border border-iv-line">
            <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3 mb-2">Suggest a revision</div>
            <textarea data-testid="strengthen-suggested"
              value={draft.suggested} onChange={(e) => setDraft((d) => ({ ...d, suggested: e.target.value }))}
              placeholder="Proposed wording…" className="w-full min-h-[80px] p-2.5 bg-iv-bg border border-iv-line text-[13.5px] rounded-[2px]" />
            <textarea data-testid="strengthen-reason"
              value={draft.reason} onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
              placeholder="Reason — why this wording is more accurate and what evidence supports the change." className="mt-2 w-full min-h-[60px] p-2.5 bg-iv-bg border border-iv-line text-[13px] rounded-[2px]" />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <label className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">Problem code</label>
              <select data-testid="strengthen-problem-code"
                value={draft.problem_code} onChange={(e) => setDraft((d) => ({ ...d, problem_code: e.target.value }))}
                className="text-[12.5px] py-1.5 px-2 bg-iv-bg border border-iv-line rounded-[2px]">
                {PROBLEM_CODES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <div className="ml-auto">
                <button data-testid="strengthen-submit" disabled={busy} onClick={create}
                  className="iv-btn iv-btn--primary text-[12.5px]"><Send size={13}/> Record suggestion</button>
              </div>
            </div>
          </div>

          {/* Revision history for this claim */}
          <div className="mt-6">
            <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3 mb-2">Revision history</div>
            {revisions.length ? (
              <div className="space-y-2">
                {revisions.map((r) => <RevisionRow key={r.id} r={r} onTransition={transition} disabled={busy} />)}
              </div>
            ) : (
              <div className="text-[13px] text-iv-fg-3">No revisions recorded for this claim yet.</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function RevisionRow({ r, onTransition, disabled }) {
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(r.suggested || "");
  return (
    <div className={`border p-3 bg-iv-surface ${r.status === "approved" || r.status === "user_edited" ? "border-iv-green" : r.status === "rejected" ? "border-[#8A3324]" : "border-iv-line"}`}
         data-testid={`rev-row-${r.id}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">
          {r.status.toUpperCase()} · {r.problem_code}
          {r.approved_by ? <> · approved by {r.approved_by} · {r.approved_at}</> : null}
          {r.rejected_by ? <> · rejected by {r.rejected_by} · {r.rejected_at}</> : null}
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[13px]">
        <div>
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-iv-fg-3">Original</div>
          <div className="text-iv-fg-2">{r.original}</div>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-iv-fg-3">Suggested</div>
          <div className="text-iv-fg">{r.suggested}</div>
          {r.final_wording ? (
            <div className="mt-1 text-[12.5px] text-[#0E5A3E]">Approved wording: “{r.final_wording}”</div>
          ) : null}
        </div>
      </div>
      <div className="mt-2 text-[12.5px] text-iv-fg-3 italic">Reason: {r.reason}</div>
      {editing && (
        <div className="mt-2">
          <textarea value={edited} onChange={(e) => setEdited(e.target.value)}
            className="w-full min-h-[60px] p-2 border border-iv-line bg-iv-bg text-[13px]"
            data-testid={`rev-edit-${r.id}`} />
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {r.status !== "approved" && (
          <button data-testid={`rev-approve-${r.id}`} disabled={disabled}
            onClick={() => onTransition(r.id, "approved")}
            className="iv-btn iv-btn--primary text-[11.5px]">Approve as-is</button>
        )}
        {!editing ? (
          <button data-testid={`rev-edit-toggle-${r.id}`} onClick={() => setEditing(true)}
            className="iv-btn text-[11.5px]">Approve after edit</button>
        ) : (
          <button data-testid={`rev-user-edited-${r.id}`} disabled={disabled}
            onClick={() => onTransition(r.id, "user_edited", edited)}
            className="iv-btn iv-btn--primary text-[11.5px]">Save edited &amp; approve</button>
        )}
        {r.status !== "rejected" && (
          <button data-testid={`rev-reject-${r.id}`} disabled={disabled}
            onClick={() => onTransition(r.id, "rejected")}
            className="iv-btn text-[11.5px]">Reject</button>
        )}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Stage 5 · Investor-readiness actions
 * ----------------------------------------------------------------------- */
function StageReadiness({ opp, onUpdate }) {
  const [actions, setActions] = useState(opp.readiness_actions || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setActions(opp.readiness_actions || []); }, [opp]);

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      const r = await testApi.patch(`/test/opportunities/${opp.id}/readiness/${id}`, { status });
      setActions(r.data.readiness_actions || []);
      toast.success("Readiness action updated.");
      onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to update readiness action.");
    } finally { setBusy(false); }
  };

  const grouped = { P0: [], P1: [], P2: [] };
  actions.forEach((a) => grouped[a.priority]?.push(a));

  return (
    <section data-testid="stage-readiness">
      <div className="iv-eyebrow mb-6">Stage 5 · Investor-readiness actions</div>
      <h2 className="font-serif-display text-[28px] md:text-[38px] leading-tight text-iv-fg max-w-3xl">
        Prioritised corrections before the pack is shared with investors.
      </h2>
      <p className="mt-4 text-[14px] text-iv-fg-2 max-w-2xl">
        No fabricated market statistics, ticket-size expectations or performance benchmarks. These items
        are drawn directly from the claim register.
      </p>
      <div className="mt-8 space-y-8">
        {["P0", "P1", "P2"].map((p) => (
          grouped[p].length ? (
            <div key={p} data-testid={`readiness-group-${p}`}>
              <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-iv-fg-2 mb-3">
                {p === "P0" ? "Critical · Address before diligence" : p === "P1" ? "Recommended · Strengthen before diligence" : "Optional · Nice-to-have"}
              </div>
              <div className="space-y-2">
                {grouped[p].map((a) => (
                  <div key={a.id} className={`border-l-[3px] p-4 bg-iv-surface ${p === "P0" ? "border-[#7B2D26]" : p === "P1" ? "border-[#B39352]" : "border-iv-fg-3"}`}
                       data-testid={`readiness-row-${a.id}`}>
                    <div className="text-[14.5px] text-iv-fg leading-relaxed">{a.action}</div>
                    <div className="mt-2 text-[13px] text-iv-fg-2"><b>Why it matters.</b> {a.why_it_matters}</div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[12.5px] text-iv-fg-3">
                      <div><b className="text-iv-fg-2">Required:</b> {a.document_or_information_required}</div>
                      <div><b className="text-iv-fg-2">Suggested owner:</b> {a.suggested_owner}</div>
                      <div><b className="text-iv-fg-2">Status:</b> {a.status}</div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {["open", "in_progress", "resolved", "wont_fix"].map((s) => (
                        <button key={s} disabled={busy}
                          data-testid={`readiness-${a.id}-${s}`}
                          onClick={() => setStatus(a.id, s)}
                          className={`px-2.5 py-1 text-[11.5px] font-mono tracking-[0.14em] uppercase border ${a.status === s ? "border-iv-green text-iv-fg" : "border-iv-line text-iv-fg-3 hover:text-iv-fg"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null
        ))}
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------
 * Stage 6 · Pack review & release
 * ----------------------------------------------------------------------- */
function StagePack({ opp, pack, versions, onRelease, onResolveNavigate }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const release = async () => {
    setBusy(true);
    try {
      await testApi.post(`/test/opportunities/${opp.id}/evidence-pack/versions`, { note });
      toast.success("Pack version released. Immutable snapshot recorded.");
      setNote("");
      onRelease();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Unable to release the pack.");
    } finally { setBusy(false); }
  };

  // Draft filenames make status obvious even when the file is detached
  // from the application. Released snapshots use version_id and a
  // released- filename served by the backend.
  const draftFn = (ext) => `terravise-investor-evidence-pack-draft-v0.${ext}`;

  const downloadJSON = () => {
    fetch(`${API}/test/opportunities/${opp.id}/evidence-pack.json`, {
      headers: { Authorization: `Bearer ${getTestToken()}` }
    })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = draftFn("json");
        a.click(); URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("Unable to download draft JSON."));
  };

  const openHTML = () => {
    fetch(`${API}/test/opportunities/${opp.id}/evidence-pack.html`, {
      headers: { Authorization: `Bearer ${getTestToken()}` }
    }).then((r) => r.text()).then((html) => {
      const blob = new Blob([html], { type: "text/html" });
      const u = URL.createObjectURL(blob);
      window.open(u, "_blank");
    }).catch(() => toast.error("Unable to render draft HTML."));
  };

  const openReleasedHTML = (versionId) => {
    fetch(`${API}/test/opportunities/${opp.id}/evidence-pack.html?version_id=${versionId}`, {
      headers: { Authorization: `Bearer ${getTestToken()}` }
    }).then((r) => r.text()).then((html) => {
      const blob = new Blob([html], { type: "text/html" });
      const u = URL.createObjectURL(blob);
      window.open(u, "_blank");
    }).catch(() => toast.error("Unable to render released HTML."));
  };

  if (!pack) return null;
  const ready = pack.release_gates?.ready;
  const blocking = pack.release_gates?.blocking_issues || [];
  const readiness = pack.release_gates?.readiness_items || [];
  const blockingCount = blocking.length;
  const readinessCount = readiness.length;
  const versionLabel = ready ? `v${versions.length + 1} (working draft)` : `v0 (working draft)`;

  return (
    <section data-testid="stage-pack">
      <div className="iv-eyebrow mb-6">Stage 6 · Evidence Pack</div>
      <h2 className="font-serif-display text-[30px] md:text-[42px] leading-tight text-iv-fg max-w-3xl">
        Investor Evidence Pack — {versionLabel}
      </h2>
      <p className="mt-4 text-[14.5px] text-iv-fg-2 max-w-2xl">
        Every element below is derived from the claim register recorded in this workspace.
      </p>

      {/* Release panel */}
      <div className={`mt-8 p-5 border-l-[3px] ${ready ? "border-iv-green bg-[rgba(14,90,62,0.06)]" : "border-[#7B2D26] bg-[rgba(139,45,38,0.08)]"}`}
           data-testid="pack-release-panel">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-iv-fg-2">Workspace release gate</div>
          <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-2" data-testid="pack-issue-counts">
            {blockingCount} material blocking issue{blockingCount === 1 ? "" : "s"} · {readinessCount} additional readiness item{readinessCount === 1 ? "" : "s"}
          </div>
        </div>
        {ready ? (
          <div className="text-[14px] text-iv-fg">
            No unresolved material evidence issues remain. An authorised workspace reviewer can now approve
            this Evidence Pack version for release.
          </div>
        ) : (
          <div className="text-[14px] text-iv-fg">
            Release blocked: {blockingCount} unresolved material evidence issue{blockingCount === 1 ? "" : "s"}.
            Material evidence issues must be resolved or explicitly addressed before this Evidence Pack version
            can be approved for release. This gate governs release of the Evidence Pack within ImpactVest.
            Responsibility for any external disclosure remains with the company.
          </div>
        )}

        {/* When blocked, show the actual blocking claim(s) + resolve nav.
            When ready, show the release-note field. */}
        {!ready && (
          <div className="mt-4 space-y-2" data-testid="pack-blocking-list">
            {blocking.map((b) => (
              <div key={b.claim_id} className="bg-iv-bg border border-iv-line p-3">
                <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">Blocking · {b.status.replace("_"," ")} · materiality {b.materiality}</div>
                <div className="text-[13.5px] text-iv-fg mt-1">{b.label}</div>
                <div className="text-[12.5px] text-iv-fg-2 mt-1"><b>Why it blocks release.</b> {b.reason}</div>
                {b.next_action ? (
                  <div className="text-[12.5px] text-iv-fg-2 mt-1"><b>Required next action.</b> {b.next_action}</div>
                ) : null}
                <div className="mt-2">
                  <button data-testid={`pack-resolve-${b.claim_id}`}
                    onClick={() => onResolveNavigate(b.claim_id)}
                    className="iv-btn text-[12px]">
                    Resolve this blocking issue →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {ready && (
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Release note (optional — recorded permanently with this version)"
              data-testid="pack-release-note"
              className="flex-1 min-w-[220px] px-2.5 py-2 text-[13px] bg-iv-bg border border-iv-line" />
            <button onClick={release} disabled={busy}
              data-testid="pack-release-btn"
              className="iv-btn iv-btn--primary text-[12.5px]">
              Approve &amp; release new version
            </button>
          </div>
        )}

        {!ready && (
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <button data-testid="pack-release-btn" disabled
              className="iv-btn text-[12.5px] opacity-60 cursor-not-allowed">
              Release unavailable
            </button>
            <span className="text-[12px] text-iv-fg-3">Resolve the blocking issue above before this version can be approved.</span>
          </div>
        )}
      </div>

      {/* Readiness items — non-blocking but recommended */}
      {readinessCount > 0 && (
        <div className="mt-4 p-4 border border-iv-line bg-iv-surface" data-testid="pack-readiness-list">
          <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-iv-fg-2 mb-2">
            Additional readiness items — recommended, not blocking
          </div>
          <ul className="text-[13px] text-iv-fg-2 space-y-1.5 list-disc pl-4">
            {readiness.map((r) => (
              <li key={r.claim_id}>
                <span className="text-iv-fg">{r.label}</span>
                <span className="text-iv-fg-3"> — {r.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Draft-labelled exports */}
      <div className="mt-6 flex flex-wrap gap-3 items-center" data-testid="pack-exports">
        <button onClick={openHTML} data-testid="pack-html-btn" className="iv-btn text-[12.5px]">
          <ExternalLink size={13}/> {ready ? "Open pending HTML" : "Open draft HTML"}
        </button>
        <button onClick={downloadJSON} data-testid="pack-json-btn" className="iv-btn text-[12.5px]">
          <Download size={13}/> {ready ? "Download pending JSON" : "Download draft JSON"}
        </button>
        <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">
          {ready ? "Pending approval — not yet a released version" : "Draft — not approved for release"}
        </span>
      </div>

      {/* Version history */}
      <div className="mt-8">
        <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-iv-fg-2 mb-2">Version history</div>
        {versions.length ? (
          <table className="w-full text-[13px] border border-iv-line" data-testid="pack-version-table">
            <thead className="bg-iv-bg-2">
              <tr>
                <th className="text-left p-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">Version</th>
                <th className="text-left p-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">Released at</th>
                <th className="text-left p-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">Approver</th>
                <th className="text-left p-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">Note</th>
                <th className="text-left p-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">Snapshot</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className="border-t border-iv-line" data-testid={`pack-version-${v.version_num}`}>
                  <td className="p-2">v{v.version_num}</td>
                  <td className="p-2 text-iv-fg-2">{v.released_at}</td>
                  <td className="p-2 text-iv-fg-2">{v.released_by}</td>
                  <td className="p-2 text-iv-fg-3">{v.note}</td>
                  <td className="p-2">
                    <button onClick={() => openReleasedHTML(v.id)}
                      data-testid={`pack-open-released-${v.version_num}`}
                      className="iv-link-underline text-[12px]">Open released HTML</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-[13px] text-iv-fg-3">No versions released yet.</div>
        )}
      </div>

      {/* Executive summary block */}
      <div className="mt-8 iv-canvas rounded-[3px] p-6 max-w-3xl" data-testid="pack-exec-summary">
        <div className="iv-canvas-eyebrow mb-3">Executive evidence summary</div>
        <div className="text-[14px] text-[var(--ivc-ivory-2)] leading-relaxed">
          Supported {pack.executive_summary.counts.supported} · Partially {pack.executive_summary.counts.partially_supported} ·
          Unsupported {pack.executive_summary.counts.unsupported} · Conflicting {pack.executive_summary.counts.conflicting_evidence} ·
          Insufficient {pack.executive_summary.counts.insufficient_evidence}.
          Material-high claims: {pack.executive_summary.material_high}. Blocking issues:{" "}
          {blockingCount}. Additional readiness items: {readinessCount}.
        </div>
      </div>

      {/* Limitations */}
      <div className="mt-8 bg-iv-bg-2 p-5 border border-iv-line max-w-3xl" data-testid="pack-limitations">
        <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-iv-fg-2 mb-2">Limitations &amp; non-assurance notice</div>
        <ul className="text-[12.5px] text-iv-fg-2 space-y-1.5 leading-relaxed list-disc pl-4">
          {pack.limitations.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
        <p className="mt-3 text-[12.5px] text-iv-fg-2 italic">{pack.non_assurance_notice}</p>
      </div>
    </section>
  );
}
